import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    heading = "heading",
    subheading = "subheading",
    variant = "variant",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    disableButton = "disableButton",
    openNewTab = "openNewTab",
    imageRightSide = "imageRightSide",
}

interface Props extends WidgetProps {
    fields: {
        [fields.background]: "image";
        [fields.image]: string;
        [fields.disableButton]: boolean;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.openNewTab]: boolean;
        [fields.imageRightSide]: boolean;
    };
}

export interface ImageTextStyles {
    wrapperStyles?: InjectableCss;
    imageWrapper?: InjectableCss;
    headingStyles?: InjectableCss;
    contentWrapper?: InjectableCss;
    boxButton?: ButtonPresentationProps;
    heading?: TypographyPresentationProps;
    subheading?: TypographyPresentationProps;
}

export const imageTextStyles: ImageTextStyles = {
    wrapperStyles: {
        css: css`
            display: flex;
            background: ${getColor("primary.contrast")};\
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            margin: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },

    imageWrapper: {
        css: css`
            width: 50%;
            > div {
                width: 100%;
                img {
                    height: 100%;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            margin: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    boxButton: {
        css: css`
            display: flex;
            align-items: center;
            letter-spacing: 1px;
            font-weight: 500;
            font-size: 14px;
            padding: 15px 60px;
            margin-top: 28px;
        `,
    },

    headingStyles: {
        css: css`
            background-color: #f4f5f7;
            width: 50%;
            padding: 57px 40px 40px;
            align-items: center;
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            margin: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
            h3 {
                margin: 2rem 0;
            }
            p:first-child {
                border-top: 3px solid #1e69a3;
                padding-top: 33px;
                font-weight: bold;
                font-size: 24px;
                min-width: 300px;
                text-align: center;
            }
            p:nth-child(2) {
                padding: 0px 55px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 0px 55px;
                            `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    contentWrapper: {
        css: css`
            max-width: 750px;
            align-items: center;
            display: flex;
            flex-direction: column;
        `,
    },
};

const styles = imageTextStyles;

export const ImageContentBox: React.FC<Props> = props => {
    const { url } = useGetLink(props.fields.buttonLink);
    const history = useHistory();
    const onClick = () => {
        if (!url) {
            return;
        }
        if (url.startsWith("http")) {
            window.open(url, "_blank");
        } else {
            props.fields.openNewTab ? window.open(url, "_blank") : history.push(url);
        }
    };

    let imgAlignStyles = "order: 0";
    let textAlignStyles = "align-self: flex-start";
    if(props.fields.imageRightSide) {
        imgAlignStyles = "order: 1";
        textAlignStyles = "align-self: flex-end";
    }

    const imgWrapperStyles = {
        css: css`
            ${styles.imageWrapper?.css || ""}
            ${imgAlignStyles}
        `,
    };

    const textWrapperStyles = {
        css: css`
            ${styles.contentWrapper?.css || ""}
            ${textAlignStyles}
        `,
    };

    return (
        <StyledWrapper {...styles.wrapperStyles} className="wrapper-styles">
            <StyledWrapper {...imgWrapperStyles}>
                <LazyImage src={props.fields.image}></LazyImage>
            </StyledWrapper>
            <StyledWrapper {...styles.headingStyles}>
                <StyledWrapper {...textWrapperStyles}>
                    {parse(props.fields.heading, parserOptions)}
                    {parse(props.fields.subheading, parserOptions)}
                    {!props.fields.disableButton && (
                        <Button {...styles.boxButton} variant={props.fields.variant} onClick={onClick}>
                            {props.fields.buttonLabel}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ImageContentBox,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [{ displayName: "Image", value: "image" }],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },

            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Summary",
                defaultValue: "",
            },
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openNewTab,
                displayName: "Open in a new tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.imageRightSide,
                displayName: "Image Right Align",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            }
        ],
    },
};

export default widgetModule;
